/**
 * AVA-475 — Mock stand-in for the company tolerance library (D3).
 *
 * The admin screen that maintains these is a later slice; the demo only needs
 * the lookup interaction, so TolerancePicker reads this file directly and
 * dispatches `VERIFICATION/setTolerance` with the chosen triple.
 *
 * ---------------------------------------------------------------------------
 * UNITS — read before touching a number in this file
 * ---------------------------------------------------------------------------
 * Every `tolerance` value here is MILLIMETRES.
 *
 * That matches `point.tolerance` and the deltas produced by
 * `utils/pointMath.computeDeltas`, which scales by the model's resolved
 * `mmPerViewerUnit` on the way out — so tolerance (mm) and deltas (mm) are
 * compared like for like by `evaluatePoint`.
 *
 * It does NOT match a coordinate. `design`, `asBuilt`, their offsets and every
 * stored position in this feature are VIEWER units — metres on some facilities,
 * MILLIMETRES on others (`utils/modelUnits.js`). Never feed a number from this
 * file into a position, and never scale one of these by 1000: that constant was
 * the feature's original unit bug, and on a millimetre facility the conversion
 * it stands for is 1.
 *
 * ---------------------------------------------------------------------------
 * WHY THERE IS NO "Span" OR "Pipe Fall" ENTRY
 * ---------------------------------------------------------------------------
 * The Results panel's check vocabulary also carries `Span` (+25 mm, one-sided)
 * and `Pipe Fall` (>= 5 mm/m). Neither is a per-axis position tolerance, and
 * the point model can only express a symmetric per-axis triple — the action is
 * `setTolerance({ x, y, z })` and the comparison is `|d| <= tol` on each axis
 * independently. An entry that filled x/y/z with 25 from a span rule would put
 * a number on a conformance report that the rule never authorised, so span and
 * fall are deliberately absent rather than approximated. They belong to a
 * different check type, not to this library.
 *
 * ---------------------------------------------------------------------------
 * THE 3 mm COUPLING
 * ---------------------------------------------------------------------------
 * `verification.module.js` seeds every new point with a private
 * `DEFAULT_TOLERANCE = { x: 3, y: 3, z: 3 }`. That const is not exported, and a
 * data file must stay import-free (see below), so "3 mm is the default" is
 * declared in two places. `DEFAULT_TOLERANCE_ID` below names the entry that
 * mirrors it, and the scratch assertion script asserts the triple — if either
 * side ever changes, the assertion fails instead of the picker silently
 * showing "no library entry selected" on every fresh point.
 *
 * ---------------------------------------------------------------------------
 * NO IMPORTS, AND EVERYTHING IS DEEP-FROZEN
 * ---------------------------------------------------------------------------
 * This file imports nothing on purpose: the Node assertion script under
 * `.claude/scratch/` evaluates the source directly (the package is CJS, so it
 * cannot `require` an ESM `.js`), and an import statement would break that.
 * For the same reason every export uses the plain `export const` /
 * `export function` form.
 *
 * The array, each entry and each nested `tolerance` object are all frozen.
 * `Object.freeze` is shallow, so freezing only the array would leave
 * `entry.tolerance` writable — a picker that bound it straight into three
 * v-text-fields would edit the shared library for the rest of the session and
 * hand the edited numbers to every later point. Bind `resolveTolerance(id)`
 * instead: it returns a fresh, mutable copy.
 */

/** Disciplines an entry can belong to. Joins to `supportMock.discipline`. */
export const TOLERANCE_DISCIPLINE = Object.freeze({
  MECHANICAL: 'MECHANICAL',
  HVAC: 'HVAC',
});

/**
 * The entry whose triple mirrors the module's own `DEFAULT_TOLERANCE`, so the
 * picker can highlight something sensible on a brand-new point.
 */
export const DEFAULT_TOLERANCE_ID = 'tol-3';

/**
 * Freeze an entry and its nested tolerance in one step, so no entry below can
 * be added half-frozen by accident.
 */
const frozenEntry = (entry) => Object.freeze({
  ...entry,
  tolerance: Object.freeze({ ...entry.tolerance }),
});

/**
 * The library itself. Ordered loosest-last so the dropdown reads tightest to
 * widest.
 *
 * Entry shape (uniform — every key is present on every entry):
 *   id               stable identifier, the value a picker emits
 *   name             display label, already carrying U+00B1 and the unit
 *   discipline       TOLERANCE_DISCIPLINE value
 *   tolerance        { x, y, z } in MILLIMETRES (frozen)
 *   sourceDoc        spec clause this came from, or null for a generic entry
 *   rulePackName     rule pack, or null
 *   rulePackRevision rule pack revision, or null
 *
 * Only `tol-hpc-pos` carries a real citation — it is the position tolerance the
 * Results panel's X/Y/Z Position checks already quote. The rest are generic
 * demo entries with no spec behind them, and say so by holding null rather than
 * borrowing a clause reference they were not derived from.
 */
export const toleranceLibraryMock = Object.freeze([
  frozenEntry({
    id: DEFAULT_TOLERANCE_ID,
    name: 'Standard support · ±3 mm',
    discipline: TOLERANCE_DISCIPLINE.MECHANICAL,
    tolerance: { x: 3, y: 3, z: 3 },
    sourceDoc: null,
    rulePackName: null,
    rulePackRevision: null,
  }),
  frozenEntry({
    id: 'tol-5',
    name: 'General mechanical · ±5 mm',
    discipline: TOLERANCE_DISCIPLINE.MECHANICAL,
    tolerance: { x: 5, y: 5, z: 5 },
    sourceDoc: null,
    rulePackName: null,
    rulePackRevision: null,
  }),
  frozenEntry({
    id: 'tol-plan-elev',
    name: 'Pipe support · plan ±10 mm / elevation ±5 mm',
    discipline: TOLERANCE_DISCIPLINE.MECHANICAL,
    tolerance: { x: 10, y: 10, z: 5 },
    sourceDoc: null,
    rulePackName: null,
    rulePackRevision: null,
  }),
  frozenEntry({
    id: 'tol-10',
    name: 'Secondary steel · ±10 mm',
    discipline: TOLERANCE_DISCIPLINE.MECHANICAL,
    tolerance: { x: 10, y: 10, z: 10 },
    sourceDoc: null,
    rulePackName: null,
    rulePackRevision: null,
  }),
  frozenEntry({
    id: 'tol-hv',
    name: 'HVAC duct support · ±15 mm',
    discipline: TOLERANCE_DISCIPLINE.HVAC,
    tolerance: { x: 15, y: 15, z: 15 },
    sourceDoc: null,
    rulePackName: null,
    rulePackRevision: null,
  }),
  frozenEntry({
    id: 'tol-hpc-pos',
    name: 'Pipe support position · ±60 mm',
    discipline: TOLERANCE_DISCIPLINE.MECHANICAL,
    tolerance: { x: 60, y: 60, z: 60 },
    sourceDoc: 'HPC-DITCTR-2021-EN-0567 §3.1.2 Table 1',
    rulePackName: 'Mechanical · Pipe Supports v1',
    rulePackRevision: 'Rev B',
  }),
]);

/**
 * Look an entry up by id. Returns the FROZEN library entry — safe to read and
 * bind to a label, never safe to mutate. Null for an unknown or missing id.
 */
export function findToleranceEntry(id) {
  if (!id) return null;
  return toleranceLibraryMock.find((entry) => entry.id === id) || null;
}

/**
 * The mutable form: a NEW `{ x, y, z }` on every call, safe to bind straight
 * into numeric fields or hand to `setTolerance`. Null for an unknown id.
 *
 * Prefer this over reading `entry.tolerance` — that object is frozen, and an
 * SFC `<script>` block is strict-mode ES module code, so writing to it throws
 * a TypeError rather than failing quietly.
 */
export function resolveTolerance(id) {
  const entry = findToleranceEntry(id);
  if (!entry) return null;
  return { x: entry.tolerance.x, y: entry.tolerance.y, z: entry.tolerance.z };
}

/**
 * Reverse lookup: which library entry (if any) does this triple correspond to?
 * Lets the dropdown mark the active row without storing an id on the point.
 *
 * Matching is EXACT on all three axes — no epsilon window. A window would let
 * two entries claim one triple and make the highlight ambiguous, and a
 * hand-edited tolerance genuinely is "no library entry", which is what null
 * means here.
 */
export function findMatchingToleranceEntry(tolerance) {
  if (!tolerance) return null;
  return toleranceLibraryMock.find((entry) => (
    entry.tolerance.x === tolerance.x
    && entry.tolerance.y === tolerance.y
    && entry.tolerance.z === tolerance.z
  )) || null;
}
